import {
  Grid,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';

import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';

import {
  CrossplaneResourcesTableSelector,
  CrossplaneOverviewCardSelector,
  CrossplaneResourceGraphSelector,
  useResourceGraphAvailable,
  useResourcesListAvailable,
  IfCrossplaneOverviewAvailable,
  IfCrossplaneResourceGraphAvailable,
  IfCrossplaneResourcesListAvailable,
} from '@terasky/backstage-plugin-crossplane-resources-frontend';

import {
  EntityAzurePipelinesContent,
  isAzureDevOpsAvailable,
} from '@backstage-community/plugin-azure-devops';

import {
  EntityFluxHelmReleasesCard,
  EntityFluxKustomizationsCard,
  EntityFluxGitRepositoriesCard,
  EntityFluxSourcesCard,
} from '@backstage-community/plugin-flux';
import {
  EntityOverviewDashboardViewer,
  EntityGrafanaDashboardsCard,
} from '@backstage-community/plugin-grafana';

import { useEntity } from '@backstage/plugin-catalog-react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { TextField, Paper, Alert, CircularProgress, FormControl, InputLabel, Select, MenuItem, Link as MuiLink } from '@mui/material';
import { EntityScaffolderContent } from '@terasky/backstage-plugin-entity-scaffolder-content';
import { RepoUrlPickerFieldExtension, OwnerPickerFieldExtension, EntityPickerFieldExtension } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import {
  GitHubAppRepoPickerFieldExtension,
  GitHubRepoPickerFieldExtension,
} from '../../scaffolder';

// Resource Edit Form Component
const ResourceEditForm = () => {
  const { entity } = useEntity();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [prUrl, setPrUrl] = useState<string | null>(null);
  const [prNumber, setPrNumber] = useState<number | null>(null);

  const entitySpec = (entity.spec as any) || {};
  const definition = entitySpec.definition || {};
  const crossplaneSpec = definition.spec || entitySpec;
  const metadataAnnotations = entity.metadata.annotations || {};
  const suffixField = 'suffix' in crossplaneSpec ? 'suffix' : 'name_suffix';

  const [formData, setFormData] = useState({
    product: (crossplaneSpec as any)?.product || '',
    environment: (crossplaneSpec as any)?.environment || 'dev',
    region: (crossplaneSpec as any)?.region || 'aue',
    suffix: (crossplaneSpec as any)?.[suffixField] || '001',
    description:
      entity.metadata.description ||
      metadataAnnotations['terasky.backstage.io/description'] ||
      '',
    owner:
      (entity.spec as any)?.owner ||
      metadataAnnotations['terasky.backstage.io/owner'] ||
      '',
  });

  const handleChange = (field: string) => (e: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccess(false);
    setError(null);
    setPrUrl(null);
    setPrNumber(null);

    try {
      // Extract GitHub info from annotations
      const editUrl = entity.metadata.annotations?.['backstage.io/edit-url'] || '';
      
      if (!editUrl) {
        throw new Error('Edit URL not found in resource metadata');
      }

      // Parse GitHub URL to extract owner/repo/path
      // Expected format: https://github.com/CTM-Engineering/app-platform-backstage/edit/CPT-24-backstage-prod/flux/apps/...
      const urlParts = editUrl.match(/github\.com\/([^\/]+)\/([^\/]+)\/edit\/([^\/]+)\/(.+)/);
      if (!urlParts) {
        throw new Error('Invalid GitHub edit URL format');
      }

      const [, owner, repo, branch, filePath] = urlParts;

      // Call backend API to create PR
      const response = await fetch('/api/scaffolder/resource-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          owner,
          repo,
          branch,
          filePath,
          resourceName: entity.metadata.name,
          updates: {
            product: formData.product,
            environment: formData.environment,
            region: formData.region,
            suffix: formData.suffix,
            description: formData.description,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to create PR: ${response.statusText}`);
      }

      const result = await response.json();
      setPrUrl(result.prUrl);
      setPrNumber(result.prNumber);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update resource');
    } finally {
      setSubmitting(false);
    }
  };

  const environments = ['dev', 'staging', 'prod', 'qa'];
  const regions = ['aue', 'use', 'eur', 'ase'];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && prUrl && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
              ✓ Pull Request Created Successfully!
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              PR #{prNumber}: Changes are ready for review
            </Typography>
            <MuiLink
              href={prUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 'bold' }}
            >
              View Pull Request →
            </MuiLink>
          </Alert>
        )}
      </Grid>

      {!success && (
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Product"
                    value={formData.product}
                    onChange={handleChange('product')}
                    placeholder="e.g., lb, gprof"
                    disabled={submitting}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Owner"
                    value={formData.owner}
                    onChange={handleChange('owner')}
                    placeholder="e.g., sub-platform-services-provider"
                    disabled={submitting}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Environment</InputLabel>
                    <Select
                      value={formData.environment}
                      label="Environment"
                      onChange={handleChange('environment')}
                      disabled={submitting}
                    >
                      {environments.map(env => (
                        <MenuItem key={env} value={env}>
                          {env.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Region</InputLabel>
                    <Select
                      value={formData.region}
                      label="Region"
                      onChange={handleChange('region')}
                      disabled={submitting}
                    >
                      {regions.map(reg => (
                        <MenuItem key={reg} value={reg}>
                          {reg.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Suffix"
                    value={formData.suffix}
                    onChange={handleChange('suffix')}
                    placeholder="e.g., 001"
                    disabled={submitting}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.description}
                    onChange={handleChange('description')}
                    multiline
                    rows={3}
                    disabled={submitting}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" spacing={2}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={submitting}
                      endIcon={submitting && <CircularProgress size={20} />}
                    >
                      {submitting ? 'Creating Pull Request...' : 'Create Pull Request'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setFormData({
                          product: (crossplaneSpec as any)?.product || '',
                          environment: (crossplaneSpec as any)?.environment || 'dev',
                          region: (crossplaneSpec as any)?.region || 'aue',
                          suffix: (crossplaneSpec as any)?.suffix || '001',
                          description: entity.metadata.description || '',
                          owner: (entity.spec as any)?.owner || '',
                        });
                        setError(null);
                      }}
                      disabled={submitting}
                    >
                      Reset
                    </Button>
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Changes will be submitted as a pull request on GitHub for review before merging.
                  </Typography>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>
      )}

      {success && prUrl && (
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Pull Request Details
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography color="textSecondary">Resource</Typography>
                <Typography sx={{ fontWeight: 'bold' }}>
                  {entity.metadata.name}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography color="textSecondary">PR Number</Typography>
                <Typography sx={{ fontWeight: 'bold' }}>
                  #{prNumber}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography color="textSecondary" sx={{ mb: 1 }}>
                  Changes:
                </Typography>
                <Typography component="div" variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: '#f5f5f5', p: 1.5, borderRadius: 1 }}>
                  {`product: ${formData.product}\n`}
                  {`environment: ${formData.environment}\n`}
                  {`region: ${formData.region}\n`}
                  {`suffix: ${formData.suffix}${formData.description ? '\n' + 'description: ' + formData.description : ''}`}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  href={prUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth
                >
                  Open Pull Request in GitHub
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSuccess(false);
                    setPrUrl(null);
                    setPrNumber(null);
                  }}
                  fullWidth
                >
                  Make Another Edit
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

const crossplaneOverviewContent = (
  <Grid container spacing={3}>
    <Grid item xs={6}>
      <EntityAboutCard  />
    </Grid>
    <IfCrossplaneOverviewAvailable>
      <Grid item xs={6}>
        <CrossplaneOverviewCardSelector />
      </Grid>
    </IfCrossplaneOverviewAvailable>
    <Grid item xs={12} md={4}>
      <EntityLinksCard />
    </Grid>
  </Grid>
);

// Create the Crossplane entity page component with permission checks
const CrossplaneEntityPage = () => {
  const isResourcesListAvailable = useResourcesListAvailable();
  const isResourceGraphAvailable = useResourceGraphAvailable();

  return (
    <EntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        {crossplaneOverviewContent}
      </EntityLayout.Route>

      <EntityLayout.Route if={isResourcesListAvailable} path="/crossplane-resources" title="Crossplane Resources">
        <IfCrossplaneResourcesListAvailable>
          <CrossplaneResourcesTableSelector />
        </IfCrossplaneResourcesListAvailable>
      </EntityLayout.Route>

      <EntityLayout.Route if={isResourceGraphAvailable} path="/crossplane-graph" title="Crossplane Graph">
        <IfCrossplaneResourceGraphAvailable>
          <CrossplaneResourceGraphSelector />
        </IfCrossplaneResourceGraphAvailable>
      </EntityLayout.Route>
    </EntityLayout>
  );
};

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const isNotMonitoringEntity = (entity: { metadata: { name: string } }) =>
  entity.metadata.name !== 'monitoring';

const cicdContent = (
  // Azure DevOps pipelines integration for build and release visibility
  <EntitySwitch>
    {/* Azure DevOps Pipelines - Shows builds and releases */}
    <EntitySwitch.Case if={isAzureDevOpsAvailable}>
      <EntityAzurePipelinesContent defaultLimit={25} />
    </EntitySwitch.Case>

    {/* Fallback for entities without CI/CD integration */}
    <EntitySwitch.Case>
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              No CI/CD available for this entity
            </Typography>
            <Typography variant="body1">
              You need to add an annotation to your component if you want to
              enable CI/CD for it. You can read more about annotations in
              Backstage by clicking the button below.
            </Typography>
            <Button
              variant="contained"
              href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
            >
              Read more
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3}>
    {entityWarningContent}
    <Grid
      item
      xs={12}
      md={10}
      sx={{
        height: { xs: 560, md: 'calc(100vh - 290px)' },
        minHeight: 560,
      }}
    >
      <EntityOverviewDashboardViewer />
    </Grid>
    <Grid item xs={12} md={2}>
      <EntityGrafanaDashboardsCard />
    </Grid>
    <Grid item xs={12}>
      <EntityLinksCard />
    </Grid>
  </Grid>
);

const monitoringOverviewContent = (
  <Grid container spacing={3}>
    {entityWarningContent}
    <Grid item xs={6}>
      <EntityAboutCard />
    </Grid>
    <Grid item xs={12} md={6}>
      <EntityCatalogGraphCard height={400} />
    </Grid>
    <Grid item xs={12} md={4}>
      <EntityLinksCard />
    </Grid>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isNotMonitoringEntity} path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      if={entity => isNotMonitoringEntity(entity) && isKubernetesAvailable(entity)}
      path="/kubernetes"
      title="Kubernetes"
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route if={isNotMonitoringEntity} path="/api" title="API">
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item xs={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route if={isNotMonitoringEntity} path="/dependencies" title="Dependencies">
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <EntityDependsOnComponentsCard  />
        </Grid>
        <Grid item xs={6}>
          <EntityDependsOnResourcesCard  />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route if={isNotMonitoringEntity} path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isNotMonitoringEntity} path="/flux" title="Flux CD">
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <EntityFluxHelmReleasesCard />
        </Grid>
        <Grid item xs={6}>
          <EntityFluxKustomizationsCard />
        </Grid>
        <Grid item xs={6}>
          <EntityFluxGitRepositoriesCard />
        </Grid>
        <Grid item xs={6}>
          <EntityFluxSourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const WebsiteEntityPage = () => {
  const configApi = useApi(configApiRef);
  const useStandardMonitoringOverview =
    configApi.getOptionalBoolean('app.monitoring.standardOverview') ?? false;
  const showEntityTabs = (entity: { metadata: { name: string } }) =>
    isNotMonitoringEntity(entity) || useStandardMonitoringOverview;

  return <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <EntitySwitch>
        <EntitySwitch.Case
          if={entity =>
            entity.metadata.name === 'monitoring' &&
            useStandardMonitoringOverview
          }
        >
          {monitoringOverviewContent}
        </EntitySwitch.Case>
        <EntitySwitch.Case>{overviewContent}</EntitySwitch.Case>
      </EntitySwitch>
    </EntityLayout.Route>

    <EntityLayout.Route if={showEntityTabs} path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      if={entity => showEntityTabs(entity) && isKubernetesAvailable(entity)}
      path="/kubernetes"
      title="Kubernetes"
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route if={showEntityTabs} path="/dependencies" title="Dependencies">
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item xs={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route if={showEntityTabs} path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={showEntityTabs} path="/flux" title="Flux CD">
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <EntityFluxHelmReleasesCard />
        </Grid>
        <Grid item xs={6}>
          <EntityFluxKustomizationsCard />
        </Grid>
        <Grid item xs={6}>
          <EntityFluxGitRepositoriesCard />
        </Grid>
        <Grid item xs={6}>
          <EntityFluxSourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>;
};

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      <WebsiteEntityPage />
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('crossplane-claim')}>
      <CrossplaneEntityPage />
    </EntitySwitch.Case>
    <EntitySwitch.Case if={isComponentType('crossplane-xr')}>
      <CrossplaneEntityPage />
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>

  </EntitySwitch>
);

const resourceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {crossplaneOverviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/diagram" title="Diagram">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityCatalogGraphCard height={700} />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/edit" title="Edit">
      <ResourceEditForm />
    </EntityLayout.Route>
  </EntityLayout>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityCatalogGraphCard  height={400} />
        </Grid>
        <Grid item xs={12} md={4}>
          <EntityLinksCard />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <EntityProvidingComponentsCard />
            </Grid>
            <Grid item xs={6}>
              <EntityConsumingComponentsCard />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard  />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard  />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard  />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard  />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={6}>
          <EntityAboutCard  />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityCatalogGraphCard  height={400} />
        </Grid>
        <Grid item xs={12} md={4}>
          <EntityLinksCard />
        </Grid>
        <Grid item xs={8}>
          <EntityHasComponentsCard  />
        </Grid>
        <Grid item xs={6}>
          <EntityHasApisCard  />
        </Grid>
        <Grid item xs={6}>
          <EntityHasResourcesCard  />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
     <EntityLayout.Route path="/scaffolder" title="Templates">
      <EntityScaffolderContent
        templateGroupFilters={[
          {
            title: 'System Templates',
            filter: (_entity, template) =>
              template.metadata?.labels?.forEntity === 'system'
            //  && entity.spec?.owner == 'platform-team'
            ,
          },
        ]}
        buildInitialState={(entity, template) => ({
          xrName: 'xr-' + entity.metadata.name.toLowerCase() + '-' + template.metadata?.annotations?.['azure/resource-abbreviation']?.toLowerCase(),
          xrNamespace: entity.metadata.name.toLowerCase(),
          owner: entity.spec?.owner || 'platform-team',
          system: entity.metadata.name.toLowerCase(),
          environment: 'development',
          crossplane : {
            compositionUpdatePolicy: 'Automatic',
            compositionSelectionStrategy:'runtime'
          },
          repoUrl: 'github.com?owner=CTM-Engineering&repo=app-platform-backstage',
          targetBranch: 'CPT-24-backstage-prod',

          // Add other initial state mappings
        })}
        ScaffolderFieldExtensions={
          <ScaffolderFieldExtensions>
            <GitHubAppRepoPickerFieldExtension />
            <RepoUrlPickerFieldExtension />
            <OwnerPickerFieldExtension />
            <EntityPickerFieldExtension />
            <GitHubRepoPickerFieldExtension />
          </ScaffolderFieldExtensions>
        }
      />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={6}>
          <EntityAboutCard  />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityCatalogGraphCard  height={400} />
        </Grid>
        <Grid item xs={6}>
          <EntityHasSystemsCard  />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('resource')} children={resourceEntityPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
